import React, { createContext, useContext, useState } from 'react';
import {
	GetRecommendationsQuery,
	QuizSource,
	useGetRecommendationsQuery,
} from 'src/graphqlTypes/catalogServiceTypes';
import { useSearchContext } from 'src/components/Providers/SearchProvider';
import { useDecision } from '@optimizely/react-sdk';
import { toQuizAnswers } from './helpers';

export interface SortedRecommendationQuizResponses {
	feeling?: string;
	education?: string;
	goal?: string;
	interests?: string[];
	businessInterests?: string[];
	techInterests?: string[];
	healthcareInterests?: string[];
	credits?: string;
	hsGpa?: string;
	collegeGpa?: string;
	weeklyTime?: string;
	timeToComplete?: string;
}

export type RecommendationType = {
	id: string;
	uuid: string;
};

export interface RecommendationsDataInterface {
	coachMessage?: GetRecommendationsQuery['coachRecommendationMessage'];
	coachRecommendations?: RecommendationType[];
	hasQuizAnswersFromCatalogSource?: boolean;
	recommendations?: RecommendationType[];
	recommendationQuizResponses: SortedRecommendationQuizResponses;
}
export interface RecommendationsContextInterface {
	recommendationsData: RecommendationsDataInterface;
	recommendationsLoading: boolean;
	setRecommendationsData: React.Dispatch<React.SetStateAction<RecommendationsDataInterface>>;
}

export const RecommendationsContext = createContext<RecommendationsContextInterface | undefined>(
	undefined
);

export const useRecommendations = () => {
	const context = useContext(RecommendationsContext);

	if (context === undefined) {
		throw new Error('useRecommendations must be used within RecommendationsProvider.');
	}

	return context;
};

export const RecommendationsProvider: React.FC = ({ children }) => {
	const { indexAvailable } = useSearchContext();
	const [recommendationsData, setRecommendationsData] =
		useState<RecommendationsDataInterface>(undefined);
	const { loading: recommendationsLoading } = useGetRecommendationsQuery({
		variables: { version: '2' },
		skip: !indexAvailable,
		onCompleted: data => {
			const recommendations =
				data.recommendations.map(rec => {
					return {
						id: rec.program.id,
						uuid: rec.program.uuid,
					};
				}) || [];
			const recommendationQuizResponses = toQuizAnswers(data.quizResponses);
			const hasQuizAnswersFromCatalogSource = data.quizResponses.length
				? data.quizResponses.map(response => response.source).includes(QuizSource.CATALOG_FRONTEND)
				: false;
			const coachRecommendations = data.coachRecommendations.map(rec => {
				return {
					id: rec.program.id,
					uuid: rec.program.uuid,
				};
			});
			const coachMessage = data.coachRecommendationMessage;
			setRecommendationsData({
				recommendations,
				recommendationQuizResponses,
				hasQuizAnswersFromCatalogSource,
				coachMessage,
				coachRecommendations,
			});
		},
	});

	return (
		<RecommendationsContext.Provider
			value={{
				recommendationsData,
				recommendationsLoading,
				setRecommendationsData,
			}}
		>
			{children}
		</RecommendationsContext.Provider>
	);
};
